/**
 * The Executive report's words and order (DEC-190, UI_SDD §R.4). Pure functions so the
 * page tests can pin them without rendering.
 *
 * Every sentence here is built from computed numbers by a fixed template, never by the
 * AI, and none of them carries an em dash (DEC-069).
 */
import type { ExecutiveMetrics, Severity, TopFinding } from '../../types/api'
import { dollars } from '../../utils/format'

// ---------------------------------------------------------------------------
// Plain finding titles (DEC-190 decision 3)
// ---------------------------------------------------------------------------

/**
 * Plain titles by subcategory. `{file}` is replaced by the file's name (not its path).
 *
 * The keys are the subcategories the backend's agents emit, as listed in the check
 * catalogue (`src/tdia/contracts/checks.py`). Two agents emit an open set (D8's lint
 * rule ids, D10's model-chosen idiom names) and LLM agents may coin new ones, so a
 * subcategory missing from this table falls back to the category table below, and a
 * category missing from that falls back to the scanner's own `human_summary`.
 * A missing entry is therefore a less friendly title, never a wrong or empty one.
 */
const SUBCATEGORY_TITLE: Record<string, string> = {
  // COMPLEXITY (D1)
  cyclomatic_complexity: 'Simplify logic with too many branches in {file}',
  long_function: 'Split up an overgrown function in {file}',
  long_file: 'Split up a file that has grown too long: {file}',
  large_parameter_list: 'Simplify a function with too many inputs in {file}',
  deep_nesting: 'Flatten deeply nested logic in {file}',
  code_duplication: 'Remove copy-pasted code in {file}',
  generated_file_in_source: 'Stop storing a generated file as source: {file}',
  // SUPPLY_CHAIN (D2)
  known_cve: 'Upgrade an outside library with a known security hole',
  missing_lockfile: 'Pin the exact versions of outside libraries',
  undeclared_dependency: 'Declare a library the code uses but never lists',
  unused_dependency: 'Remove a library nothing uses',
  license_risk: 'Review an outside library whose licence conflicts with yours',
  undeclared_license: 'Check the licence of an outside library that records none',
  missing_license_file: 'Add a licence file to this code',
  license_declaration_conflict: 'Make the licence declarations agree',
  // TEST_COVERAGE (D3)
  untested_critical_path: 'Add tests to important code that has none in {file}',
  test_theater: 'Fix tests that check nothing in {file}',
  // ARCHITECTURE (D4)
  circular_dependency: 'Break a loop of parts that depend on each other in {file}',
  god_module: 'Split up a part that everything depends on: {file}',
  coupling_hotspot: 'Loosen a part that ties the code together: {file}',
  orphan_module: 'Remove or connect a part nothing uses: {file}',
  knowledge_concentration: 'Share knowledge of code only one person knows: {file}',
  abandoned_complex_code: 'Take ownership of complex code whose authors have left: {file}',
  hidden_coupling: 'Untangle files that always change together: {file}',
  // SECURITY (D5)
  hardcoded_secret: 'Remove a password or key written into the code in {file}',
  sql_injection: 'Close a database injection hole in {file}',
  insecure_function: 'Replace an unsafe function in {file}',
  weak_crypto: 'Replace weak encryption in {file}',
  // DOCUMENTATION (D6)
  missing_docs: 'Document code that has no explanation in {file}',
  stale_docstring: 'Update documentation that no longer matches {file}',
  self_admitted_debt: 'Resolve a shortcut the team flagged in {file}',
  // PERFORMANCE (D7)
  n_plus_one_query: 'Stop running database queries in a loop in {file}',
  sync_io_in_async: 'Remove a blocking call that slows requests in {file}',
  unbounded_load: 'Limit a query that can load everything in {file}',
  floating_promise: 'Wait for work that is started and never checked in {file}',
  // DEAD_CODE (D9)
  uncalled_function: 'Remove a function nothing calls in {file}',
  unused_export: 'Remove code nothing imports in {file}',
  commented_out_code: 'Delete commented-out code in {file}',
  long_dep_chain: 'Remove a chain of code nothing uses, starting at {file}',
  // DESIGN (D11)
  empty_catch_block: 'Stop hiding errors in {file}',
  swallowed_exception: 'Stop hiding errors in {file}',
  missing_error_boundary: 'Keep one screen error from breaking the whole page in {file}',
  inconsistent_api_shape: 'Make API responses consistent in {file}',
  missing_input_validation: 'Check incoming data before using it in {file}',
  inconsistent_error_propagation: 'Handle errors the same way everywhere in {file}',
  missing_alt_text: 'Describe images for screen readers in {file}',
  missing_accessible_name: 'Name controls for screen readers in {file}',
  click_without_keyboard: 'Make a control usable without a mouse in {file}',
  input_without_label: 'Label a form field in {file}',
  positive_tabindex: 'Fix the keyboard order in {file}',
  // COUPLING (D12)
  bidirectional_dependency: 'Stop two services calling each other both ways',
  deep_call_chain: 'Shorten a request that passes through too many services',
  mesh_density_cluster: 'Untangle a cluster of services that all talk to each other',
  high_fanout_service: 'Reduce how many services one service calls',
  chokepoint_service: 'Remove a single service everything depends on',
  transitive_coupling_blast_radius: 'Limit how far one service failure spreads',
  // INFRASTRUCTURE (D13)
  container_runs_as_root: 'Stop a container running with full permissions in {file}',
  unpinned_base_image: 'Pin a container image that can change under you in {file}',
  missing_resource_limits: 'Set resource limits for a workload in {file}',
  missing_health_probe: 'Add a health check to a workload in {file}',
  secret_in_manifest: 'Remove a password or key written into {file}',
  privileged_container: 'Remove full machine access from a container in {file}',
  public_ingress: 'Close a database or admin console open to the internet in {file}',
  overly_broad_iam: 'Narrow cloud permissions granted to everything in {file}',
  unpinned_provider: 'Pin the version of an infrastructure tool in {file}',
}

/** When the subcategory is not in the table: a title per category. */
const CATEGORY_TITLE: Record<string, string> = {
  SECURITY: 'Close a security gap in {file}',
  SUPPLY_CHAIN: 'Fix a problem with an outside library',
  CODE_STYLE: 'Tidy code that breaks the team style in {file}',
  IDIOMATIC: 'Rewrite code that ignores the language conventions in {file}',
}

function fileName(path: string): string {
  const parts = path.split('/').filter(Boolean)
  return parts.length ? parts[parts.length - 1] : path
}

/** A plain title for a finding: subcategory table, then category table, then the scanner text. */
export function plainTitle(f: Pick<TopFinding, 'category' | 'subcategory' | 'file' | 'human_summary'>): string {
  const template =
    (f.subcategory ? SUBCATEGORY_TITLE[f.subcategory] : undefined) ?? CATEGORY_TITLE[f.category]
  if (!template) return f.human_summary
  return template.replace('{file}', fileName(f.file))
}

/** One impact line per category (the calm version of the DEC-049 templates). */
export const IMPACT_LINE: Record<string, string> = {
  SECURITY: 'Closes part of your breach risk. Do it first.',
  SUPPLY_CHAIN: 'A security review would flag this. Do it first.',
  COMPLEXITY: 'Your team loses time here on every change.',
  ARCHITECTURE: 'It stops teams from working in parallel.',
  TEST_COVERAGE: 'Changes here ship without a safety net.',
  COUPLING: 'Teams wait on each other to release.',
  PERFORMANCE: 'Your users can feel this as slowness.',
  DEAD_CODE: 'Unused code that makes every change slower.',
  DOCUMENTATION: 'Slows every new hire and every incident.',
  DESIGN: 'Bugs here fail quietly and are hard to find.',
  INFRASTRUCTURE: 'Problems that show up in production, not in tests.',
  CODE_STYLE: 'Inconsistent code slows every review.',
  IDIOMATIC: 'Unusual patterns that confuse whoever works here next.',
}

export function impactLine(category: string): string {
  return IMPACT_LINE[category] ?? 'Lowers the debt you carry.'
}

export function hoursLabel(h: number): string {
  if (h < 1) return 'under 1 hour'
  const n = Math.round(h)
  return `about ${n.toLocaleString('en-US')} ${n === 1 ? 'hour' : 'hours'}`
}

// ---------------------------------------------------------------------------
// The ledger: "What to do now"
// ---------------------------------------------------------------------------

/**
 * Three findings. **Any critical finding goes first, whatever its category** (DEC-190
 * decision 3): the rule used to look for SECURITY first, so a critical SUPPLY_CHAIN
 * finding (a library with a known hole) never made the list. Then the existing DEC-049
 * rule: security, velocity (complexity, tests), structure, then the largest efforts.
 */
export function buildLedger(top: TopFinding[]): TopFinding[] {
  const picked: TopFinding[] = []
  const pick = (pred: (f: TopFinding) => boolean) => {
    const f = top.find((x) => pred(x) && !picked.includes(x))
    if (f) picked.push(f)
  }
  pick((f) => f.severity === 'critical')
  pick((f) => f.category === 'SECURITY')
  pick((f) => ['COMPLEXITY', 'TEST_COVERAGE'].includes(f.category))
  pick((f) => ['ARCHITECTURE', 'COUPLING', 'PERFORMANCE'].includes(f.category))
  for (const f of [...top].sort((a, b) => b.estimated_effort_hours - a.estimated_effort_hours)) {
    if (picked.length >= 3) break
    if (!picked.includes(f)) picked.push(f)
  }
  return picked.slice(0, 3)
}

// ---------------------------------------------------------------------------
// The decision sentence and the band
// ---------------------------------------------------------------------------

export interface DecisionParts {
  /** null when there is nothing to fix. */
  fix: string | null
  /** null when no payback can be computed (no velocity cost measured). */
  months: string | null
}

/** The title's numbers. The page sets them in mono; `decisionSentence` is the same text. */
export function decisionParts(m: Pick<ExecutiveMetrics, 'total_debt_cost_dollars' | 'break_even_months'>): DecisionParts {
  if (m.total_debt_cost_dollars <= 0) return { fix: null, months: null }
  return {
    fix: dollars(m.total_debt_cost_dollars),
    months: m.break_even_months == null ? null : m.break_even_months.toFixed(1),
  }
}

/** "A one-time fix of $116,696 pays for itself in 2.6 months." Never generated. */
export function decisionSentence(m: Pick<ExecutiveMetrics, 'total_debt_cost_dollars' | 'break_even_months'>): string {
  const p = decisionParts(m)
  if (p.fix === null) return 'This scan found nothing that needs fixing.'
  if (p.months === null) return `A one-time fix of ${p.fix}. TDIA measured no slowdown for it to pay back.`
  return `A one-time fix of ${p.fix} pays for itself in ${p.months} months.`
}

export type BandTone = 'bad' | 'good' | 'neutral' | 'critical'

export interface BandCell {
  label: string
  value: string
  suffix?: string
  pill: string
  tone: BandTone
  sub: string
}

/** The four numbers (DEC-190 decision 2). Colour lives only in the pill. */
export function bandCells(m: ExecutiveMetrics, criticalCount: number): BandCell[] {
  const roi = m.total_debt_cost_dollars > 0 ? m.annual_velocity_cost_dollars / m.total_debt_cost_dollars : null
  return [
    {
      label: 'Slowing your team',
      value: dollars(m.annual_velocity_cost_dollars),
      pill: `${Math.round(m.velocity_tax_percent * 100)}% slower delivery`,
      tone: 'bad',
      sub: 'a year, in lost time',
    },
    {
      label: 'One-time fix',
      value: dollars(m.total_debt_cost_dollars),
      pill: hoursLabel(m.total_effort_hours),
      tone: 'neutral',
      sub: 'of engineering work',
    },
    m.break_even_months == null
      ? {
          label: 'Pays back in',
          value: 'n/a',
          pill: 'no slowdown measured',
          tone: 'neutral',
          sub: 'nothing to pay back',
        }
      : {
          label: 'Pays back in',
          value: m.break_even_months.toFixed(1),
          suffix: ' months',
          pill: roi === null ? 'no fix needed' : `${roi.toFixed(1)}× return in year one`,
          tone: 'good',
          sub: 'from the time it frees',
        },
    {
      label: 'Breach risk',
      value: `${Math.round(m.breach_probability_percent)}%`,
      pill:
        criticalCount > 0
          ? `${criticalCount} critical issue${criticalCount === 1 ? '' : 's'}`
          : 'no critical issues',
      tone: criticalCount > 0 ? 'critical' : 'neutral',
      sub: 'chance in the next year',
    },
  ]
}

/** Extra cost of the same fix after each quarter of waiting (DEC-059's modeled scenario). */
export const QUARTERLY_GROWTH = 0.1
export function waitingQuarters(totalDebt: number): number[] {
  return [1, 2, 3, 4].map((q) => totalDebt * ((1 + QUARTERLY_GROWTH) ** q - 1))
}

export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

// ---------------------------------------------------------------------------
// The "More detail" accordion
// ---------------------------------------------------------------------------

export type DetailSection = 'wait' | 'severe' | 'figures' | 'method'

/** "If you wait a year" is open by default (founder, DEC-190 decision 4). */
export const DEFAULT_OPEN_SECTION: DetailSection = 'wait'

/** One section open at a time: opening one closes the other; clicking the open one closes it. */
export function toggleSection(open: DetailSection | null, clicked: DetailSection): DetailSection | null {
  return open === clicked ? null : clicked
}

export function detailHint(open: DetailSection | null): string {
  return open === null
    ? 'Four sections, all closed. Open any of them.'
    : 'One section open at a time. Opening another closes this one.'
}
